// The API's uniform error envelope (api-contract §5) as a typed exception. The message is the
// server's, which never carries tenant data or prompt content; the SPA shows it verbatim beside
// the code so an operator can quote both in a ticket.
import type { components } from "./schema";

export type ErrorEnvelope = components["schemas"]["ErrorEnvelope"];
export type ErrorDetail = components["schemas"]["ErrorDetail"];

/** Status-only fallback codes for a response that carried no envelope. */
const FALLBACK_CODES: Record<number, string> = {
  400: "invalid_request",
  401: "session_expired",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  413: "too_large",
  422: "invalid_request",
  429: "rate_limited",
  503: "unavailable",
};

/** Codes the contract marks retryable regardless of their status (api-contract §5). */
const RETRYABLE_CODES = new Set(["rollups_pending", "session_on_other_worker", "audit_unavailable"]);

export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details: readonly ErrorDetail[];
  /** The answer's Retry-After in whole seconds, when it carried one (a daily budget's 429). */
  readonly retryAfterSeconds: number | null;

  constructor(status: number, code: string, message: string, requestId = "", details: readonly ErrorDetail[] = [], retryAfterSeconds: number | null = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Whether trying the same request again may succeed (429, 5xx, or a retryable code). */
  get retryable(): boolean {
    return this.status === 429 || this.status >= 500 || RETRYABLE_CODES.has(this.code);
  }
}

/** The request never produced an HTTP response (offline, DNS, aborted connection). */
export class NetworkError extends ApiError {
  constructor() {
    super(0, "network_error", "The admin service could not be reached.");
  }

  override get retryable(): boolean {
    return true;
  }
}

function isEnvelope(body: unknown): body is ErrorEnvelope {
  if (typeof body !== "object" || body === null) return false;
  const error = (body as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === "string" && typeof message === "string";
}

/** A Retry-After header in whole seconds; null when absent or not a non-negative number of seconds. */
export function retryAfterSeconds(header: string | null): number | null {
  if (header === null || !/^\d+$/.test(header.trim())) return null;
  return Number(header.trim());
}

/** Map a non-2xx response body (already parsed, or null) to an ApiError, with its Retry-After header if any. */
export function toApiError(status: number, body: unknown, retryAfter: string | null = null): ApiError {
  if (isEnvelope(body)) {
    const { code, message, requestId, details } = body.error;
    return new ApiError(status, code, message, requestId ?? "", details ?? [], retryAfterSeconds(retryAfter));
  }
  const code = FALLBACK_CODES[status] ?? (status >= 500 ? "internal" : "http_error");
  return new ApiError(status, code, `The admin service answered HTTP ${status}.`);
}
